import { FC } from 'react';
import { Icon, Tooltip } from '@kontentino/ui';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import ComparisonBadge from './ComparisonBadge';
import { CHART_COLORS } from 'app/modules/analytics/components/shared/charts/chartConfig';
import type { ChangeDirection } from 'api/new-analytics/types';

interface PeriodNumberProps {
  variant: 'current' | 'previous';
  color: string;
  label: string;
  value: string;
  badge?: number;
  badgeDirection?: ChangeDirection;
}

const PeriodNumber: FC<PeriodNumberProps> = ({
  variant,
  color,
  label,
  value,
  badge,
  badgeDirection,
}) => {
  const isCurrent = variant === 'current';
  return (
    <div className="tw-flex tw-items-start tw-gap-2">
      <div className="tw-flex tw-items-center tw-py-[2px]">
        <div
          className="tw-h-3 tw-w-3 tw-shrink-0 tw-rounded"
          style={{ backgroundColor: color }}
          data-cy={`analytics-graph-period-swatch-${variant}`}
        />
      </div>
      <div className="tw-flex tw-min-w-0 tw-flex-col tw-items-start tw-gap-2">
        <span
          className={clsx('tw-text-xs tw-font-medium tw-leading-4', {
            'tw-truncate tw-text-grayscale-180': isCurrent,
            'tw-text-grayscale-100': !isCurrent,
          })}
        >
          {label}
        </span>
        <div className="tw-flex tw-max-w-full tw-items-center tw-gap-1 tw-overflow-hidden">
          <span
            className={clsx('tw-font-semibold tw-text-grayscale-180', {
              'tw-text-xl': isCurrent,
              'tw-text-sm': !isCurrent,
            })}
            data-cy={`analytics-graph-period-value-${variant}`}
          >
            {value}
          </span>
          {isCurrent &&
            (badge !== undefined ||
              badgeDirection === 'up' ||
              badgeDirection === 'down') && (
              <ComparisonBadge
                percentage={badge}
                direction={badgeDirection}
                showIcon={false}
              />
            )}
        </div>
      </div>
    </div>
  );
};

interface MetricComparisonHeaderProps {
  title: string;
  tooltip?: string;
  currentValue: string;
  previousValue: string;
  badge?: number;
  badgeDirection?: ChangeDirection;
  currentPeriodLabel?: string;
  previousPeriodLabel?: string;
}

const MetricComparisonHeader: FC<MetricComparisonHeaderProps> = ({
  title,
  tooltip,
  currentValue,
  previousValue,
  badge,
  badgeDirection,
  currentPeriodLabel,
  previousPeriodLabel,
}) => {
  const { t } = useTranslation();

  const currentLabel =
    currentPeriodLabel ?? t('analyticsPerformance.metrics.thisPeriod');
  const previousLabel =
    previousPeriodLabel ?? t('analyticsPerformance.metrics.prevPeriod');

  return (
    <div
      className="tw-flex tw-flex-col tw-items-start tw-gap-2"
      data-cy="analytics-graph-comparison-header"
    >
      <div className="tw-flex tw-items-center tw-gap-1">
        <span className="tw-text-md tw-font-medium tw-text-grayscale-180">
          {title}
        </span>
        {tooltip && (
          <Tooltip content={tooltip}>
            <span className="tw-flex tw-cursor-default tw-items-center tw-text-grayscale-100">
              <Icon icon={faCircleInfo} className="tw-text-xs" />
            </span>
          </Tooltip>
        )}
      </div>
      <div className="tw-flex tw-flex-wrap tw-items-start tw-gap-6">
        <PeriodNumber
          variant="current"
          color={CHART_COLORS.defaultDark}
          label={currentLabel}
          value={currentValue}
          badge={badge}
          badgeDirection={badgeDirection}
        />
        <PeriodNumber
          variant="previous"
          color={CHART_COLORS.compareLight}
          label={previousLabel}
          value={previousValue}
        />
      </div>
    </div>
  );
};

export default MetricComparisonHeader;
